// app/contents/blog-edit/[id].js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { app_url } from "../../../config";
import Loader from "../../../components/Loader";
import ImageUploadModal from "../../../components/modals/ImageUploadModal";
import Error500 from "../../../components/500/Error500";

// ─── Field config — mirrors CreateBlog constraints ───────────────────────────
const FIELD_CONFIG = {
  url_name: { min: 3, max: 256, label: "URL Name" },
  title: { min: 3, max: 256, label: "Title" },
  short_detail: {
    min: 50,
    max: 5000,
    label: "Blog Description (Short Detail)",
  },
  meta_web_title: { min: 3, max: 60, label: "Meta Web Title" },
  meta_web_description: { min: 3, max: 160, label: "Meta Web Description" },
  meta_og_title: { min: 3, max: 90, label: "Open Graph Title" },
  meta_og_description: { min: 3, max: 200, label: "Open Graph Description" },
};

// ─── Inline editable field ────────────────────────────────────────────────────
const EditableField = ({ fieldKey, value, onSave, saving, type = "text" }) => {
  const { min, max, label } = FIELD_CONFIG[fieldKey];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const len = draft.length;
  const tooShort = len > 0 && len < min;
  const tooLong = len > max;
  const isValid = len >= min && len <= max;
  const isInvalid = tooShort || tooLong;

  const counterClass =
    tooShort || tooLong
      ? "text-danger"
      : isValid
        ? "text-success"
        : "text-muted";

  const handleSave = async () => {
    if (isInvalid || len === 0) {
      toast.error(`${label} must be between ${min} and ${max} characters`);
      return;
    }
    await onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="text-muted mb-0">{label}</h6>
        {!editing && (
          <button
            className="btn btn-sm btn-danger"
            style={{ fontSize: "0.75rem" }}
            onClick={() => {
              setDraft(value || "");
              setEditing(true);
            }}
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <>
          {type === "textarea" ? (
            <textarea
              className={`form-control ${isInvalid ? "is-invalid" : isValid ? "is-valid" : ""}`}
              rows={fieldKey === "short_detail" ? 7 : 4}
              value={draft}
              maxLength={max}
              onChange={(e) => setDraft(e.target.value)}
            />
          ) : (
            <input
              type="text"
              className={`form-control ${isInvalid ? "is-invalid" : isValid ? "is-valid" : ""}`}
              value={draft}
              maxLength={max}
              onChange={(e) => setDraft(e.target.value)}
            />
          )}

          {/* Character counter row */}
          <div className="d-flex justify-content-between mt-1">
            <small className={counterClass}>
              {len} / {max} characters
              {tooShort && ` (Min ${min})`}
            </small>
            <small className="text-muted">
              {isValid ? `${max - len} remaining` : ""}
            </small>
          </div>

          {/* Inline validation messages */}
          {tooShort && (
            <div className="text-danger small mt-1">
              <i className="bi bi-exclamation-circle me-1"></i>
              {label} is too short (minimum {min} characters)
            </div>
          )}
          {tooLong && (
            <div className="text-danger small mt-1">
              <i className="bi bi-exclamation-circle me-1"></i>
              {label} is too long (maximum {max} characters)
            </div>
          )}
          {isValid && (
            <div className="text-success small mt-1">
              <i className="bi bi-check-circle me-1"></i>
              Good length
            </div>
          )}

          <div className="mt-2 d-flex gap-2">
            <button
              className="btn btn-sm btn-warning text-white"
              onClick={handleSave}
              disabled={saving || isInvalid || len === 0}
            >
              {saving ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <i className="bi bi-check-lg me-1"></i>
              )}
              Save
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="border rounded p-3 bg-light">
          <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {value || <span className="text-muted">Not set</span>}
          </p>
          {/* <small className="text-muted">{value?.length || 0} characters</small> */}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("adminEtijwaal"))?.token;

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("content");
  const [networkIssue, setNetworkIssue] = useState(false);
  const [failed, setFailed] = useState("");
  const [savingField, setSavingField] = useState(null);

  // Title image state
  const [editingImage, setEditingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [savingImage, setSavingImage] = useState(false);

  // ── Centralised error handler ─────────────────────────────────────────────
  const handleError = (err) => {
    if (err.response) {
      if (err.response.status === 400) {
        toast.error(err.response.data.message || "Bad Request");
      } else if (err.response.status === 401) {
        navigate("/");
      } else if (err.response.status === 500) {
        toast.error(err.response.data.message || "Internal Server Error");
      } else {
        toast.error("An unexpected error occurred.");
      }
    } else {
      setNetworkIssue(true);
      toast.error("Network error. Please check your internet connection.");
      setFailed("Network error. Please check your internet connection.");
    }
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (id) fetchBlogDetails();
  }, [id]);

  const fetchBlogDetails = () => {
    setLoading(true);
    setNetworkIssue(false);
    setFailed("");
    axios
      .get(`${app_url}/api/blogs/fetchBlogByidForAdmin?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data?.success && res.data?.result) {
          setBlog(res.data.result);
        } else {
          toast.error(res.data?.message);
          navigate("/contents");
        }
      })
      .catch((err) => {
        setLoading(false);
        handleError(err);
      });
  };

  // ── Generic slug-based updater ────────────────────────────────────────────
  const updateField = (slug, payload) => {
    const body = { id: Number(id), slug, ...payload };
    return axios.post(`${app_url}/api/blogs/updateBlogById_forSlug`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const handleSaveField = (slug, payload, fieldKey, newValue) => {
    setSavingField(slug);
    updateField(slug, payload)
      .then((res) => {
        if (res.data?.success) {
          setBlog((prev) => ({ ...prev, [fieldKey]: newValue }));
          toast.success(res.data?.message);
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        handleError(err);
      })
      .finally(() => {
        setSavingField(null);
      });
  };

  // ── Title image ───────────────────────────────────────────────────────────
  const handleTitleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (!file.type.startsWith("image/png")) {
      toast.error("Please upload a PNG image file");
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      toast.error("Image size should be less than 1MB");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveTitleImage = () => {
    if (!imageFile) return;
    setSavingImage(true);

    const formData = new FormData();
    formData.append("id", id);
    formData.append("title_image", imageFile);

    axios
      .post(`${app_url}/api/blogs/updateBlogTitleImageById`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data?.success) {
          toast.success(res.data?.message);
          setEditingImage(false);
          setImageFile(null);
          setImagePreview(null);
          fetchBlogDetails();
        } else {
          toast.error(res.data?.message);
        }
      })
      .catch((err) => {
        handleError(err);
      })
      .finally(() => {
        setSavingImage(false);
      });
  };

  const handleRemoveTitleImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="parent-loader h-75">
        <Loader />
      </div>
    );
  }

  if (networkIssue) {
    return <Error500 message={failed} />;
  }

  if (!blog) {
    return (
      <div className="wrapper">
        <div className="text-center py-5">
          <i className="bi bi-exclamation-circle text-warning fs-1"></i>
          <h5 className="mt-3">Blog not found</h5>
          <p className="text-muted">
            The blog you're looking for doesn't exist.
          </p>
          <button
            className="btn btn-primary mt-1"
            onClick={() => navigate("/contents")}
          >
            Back to Contents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="heading">
            <i
              className="bi bi-arrow-left-circle-fill fs-3 me-3 my-auto pointer"
              onClick={() => window.history.back()}
            ></i>
            Blog Details
          </p>
        </div>
        <div
          className={`${blog?.isActive === 1 ? "bg-success" : "bg-danger"} btn text-white px-2 py-1 rounded`}
        >
          {blog?.isActive === 1 ? "Active" : "Inactive"}
        </div>
      </div>

      {/* ── Tabs ── */}
      <ul className="nav nav-tabs mb-4">
        {["content", "seo"].map((tab) => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
              style={{
                color: activeTab === tab ? "#f57c21" : "#000000",
                fontWeight: activeTab === tab ? "600" : "400",
              }}
            >
              {tab === "content" ? "Content" : "SEO & Metadata"}
            </button>
          </li>
        ))}
      </ul>

      {/* ════════════════════════════════════════════════════
          CONTENT TAB
      ════════════════════════════════════════════════════ */}
      {activeTab === "content" && (
        <div className="card">
          <div className="card-body">
            {/* URL Name */}
            <EditableField
              fieldKey="url_name"
              value={blog.url_name}
              saving={savingField === "url_name"}
              onSave={(val) =>
                handleSaveField("url_name", { url_name: val }, "url_name", val)
              }
            />

            {/* Dates (read-only) */}
            <div className="row mb-4">
              <div className="col-md-6">
                <h6 className="text-muted mb-1">Created</h6>
                <p className="mb-0">{formatDate(blog.createdAt)}</p>
              </div>
              <div className="col-md-6">
                <h6 className="text-muted mb-1">Last Updated</h6>
                <p className="mb-0">{formatDate(blog.updatedAt)}</p>
              </div>
            </div>

            {/* Title */}
            <EditableField
              fieldKey="title"
              value={blog.title}
              saving={savingField === "title"}
              onSave={(val) =>
                handleSaveField("title", { title: val }, "title", val)
              }
            />

            {/* Title Image */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="text-muted mb-0">Title Image</h6>
                {!editingImage && (
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ fontSize: "0.75rem" }}
                    onClick={() => setEditingImage(true)}
                  >
                    Edit
                  </button>
                )}
              </div>

              {blog.image && !editingImage && (
                <div className="border rounded p-3 bg-light">
                  <img
                    src={`${app_url}${blog.image}`}
                    alt={blog.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "200px" }}
                  />
                  <p className="text-muted mt-1 mb-0">
                    <small>{blog.image.split("/").pop()}</small>
                  </p>
                </div>
              )}

              {editingImage && (
                <>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/png"
                    onChange={handleTitleImageUpload}
                  />
                  <div className="form-text">PNG only. Max size: 1MB.</div>

                  {imagePreview && (
                    <div className="mt-3">
                      <div className="d-flex align-items-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="img-thumbnail me-3"
                          style={{
                            width: "150px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <p className="mb-1">
                            <strong>Preview:</strong> {imageFile.name}
                          </p>
                          <p className="mb-1 text-muted">
                            Size: {(imageFile.size / 1024).toFixed(2)} KB
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleRemoveTitleImage}
                          >
                            <i className="bi bi-trash me-1"></i>Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 d-flex gap-2">
                    <button
                      className="btn btn-sm btn-warning text-white"
                      onClick={handleSaveTitleImage}
                      disabled={savingImage || !imageFile}
                    >
                      {savingImage ? (
                        <span className="spinner-border spinner-border-sm me-1" />
                      ) : (
                        <i className="bi bi-check-lg me-1"></i>
                      )}
                      Save Image
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setEditingImage(false);
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      disabled={savingImage}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Short Detail */}
            <EditableField
              fieldKey="short_detail"
              value={blog.short_detail}
              type="textarea"
              saving={savingField === "short_detail"}
              onSave={(val) =>
                handleSaveField(
                  "short_detail",
                  { short_detail: val },
                  "short_detail",
                  val,
                )
              }
            />

            {/* HTML Blog Content — READ ONLY */}
            <div className="mb-4">
              <h6 className="text-muted mb-2">HTML Detail Content</h6>
              {/* Updated blog content with proper width styling */}
              <div className="blog-content-wrapper">
                {blog.description ? (
                  <div className="blog-content-grid">
                    <div className="blog-content-main">
                      <div
                        className="blog-content"
                        dangerouslySetInnerHTML={{
                          __html: blog.description,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mb-0">No content available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          SEO TAB
      ════════════════════════════════════════════════════ */}
      {activeTab === "seo" && (
        <div className="card">
          <div className="card-body">
            <EditableField
              fieldKey="meta_web_title"
              value={blog.meta_web_title}
              saving={savingField === "meta_web_title"}
              onSave={(val) =>
                handleSaveField(
                  "meta_web_title",
                  { meta_web_title: val },
                  "meta_web_title",
                  val,
                )
              }
            />

            <EditableField
              fieldKey="meta_web_description"
              value={blog.meta_web_description}
              type="textarea"
              saving={savingField === "meta_web_description"}
              onSave={(val) =>
                handleSaveField(
                  "meta_web_description",
                  { meta_web_description: val },
                  "meta_web_description",
                  val,
                )
              }
            />

            <EditableField
              fieldKey="meta_og_title"
              value={blog.meta_og_title}
              saving={savingField === "meta_og_title"}
              onSave={(val) =>
                handleSaveField(
                  "meta_og_title",
                  { meta_og_title: val },
                  "meta_og_title",
                  val,
                )
              }
            />

            <EditableField
              fieldKey="meta_og_description"
              value={blog.meta_og_description}
              type="textarea"
              saving={savingField === "meta_og_description"}
              onSave={(val) =>
                handleSaveField(
                  "meta_og_description",
                  { meta_og_description: val },
                  "meta_og_description",
                  val,
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;
